import { signInWithEmail, useSignInWithGoogle } from '@supa/auth';
import Countdown, { CountdownRendererFn } from 'react-countdown';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import FormInputText from '@components/forms/FormInputText';
import { Alert, AlertTitle, CircularProgress, Grid2, Stack, Typography } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { breadcrumb, logError } from '@utils/logging';
import useAuth from '@hooks/useAuth';
import { useNavigate } from 'react-router';
import LoadingButton from '@atoms/LoadingButton';
import useLocationQuery from '@hooks/useLocationQuery';

// NOTE: This is a workaround for the Google Sign-In button - https://stackoverflow.com/a/71421555
declare global {
    interface Window {
        handleCredentialResponse?: unknown;
    }
}

interface Inputs {
    email: string;
}

const TIMEOUT = 59000;

export default function Login() {
    const navigate = useNavigate();
    const { loading, session } = useAuth();
    const formMethods = useForm<Inputs>();
    const [sendingEmail, setSendingEmail] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [countdownTo, setCountdownTo] = useState(0);
    const query = useLocationQuery();

    const onSubmit: SubmitHandler<Inputs> = (values) => {
        console.log(`Login onSubmit`, { values });
        setSendingEmail(true);
        setSubmitted(true);

        toast
            .promise(signInWithEmail(values.email), {
                pending: 'Preparing email link...',
                success: 'Email Sent! Check you inbox for the login link',
                error: 'Error sending email login link',
            })
            .then(() => setSendingEmail(false))
            .catch((error: unknown) => {
                breadcrumb('Error sending email login link', { error });
                if (error instanceof Error) logError(error);
                setSubmitted(false);
                setSendingEmail(false);
            });
    };

    // Countdown to reset the form after TIMEOUT
    useEffect(() => {
        if (submitted) {
            setCountdownTo(Date.now() + TIMEOUT);
            const timer = setTimeout(() => {
                setSubmitted(false);
                setCountdownTo(0);
            }, TIMEOUT);
            return () => clearTimeout(timer);
        }
    }, [submitted]);

    // console.log(`Login loading`, { loading, session, authUser });

    useEffect(() => {
        if (session) {
            // Redirect to the returnTo URL if it exists or to the events page
            navigate(query.get(`returnTo`) || '/events', { replace: true });
        }
    }, [navigate, query, session]);

    return loading ? (
        <Grid2 paddingX={'49vw'} paddingY={'45vh'}>
            <CircularProgress color="secondary" size={40} />
        </Grid2>
    ) : (
        <>
            <div className="form-header">
                <h3>Sign in</h3>
            </div>
            <FormProvider {...formMethods}>
                <form
                    onSubmit={formMethods.handleSubmit(onSubmit)}
                    style={{ textAlign: 'center' }}
                    className="form-container"
                >
                    <GoogleSignInButton />

                    <Stack>
                        <FormInputText
                            name="email"
                            control={formMethods.control}
                            label="Email"
                            rules={{
                                required: true,
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: 'Please enter a valid email',
                                },
                            }}
                        />

                        <Stack>
                            <LoadingButton
                                disabled={submitted}
                                loading={sendingEmail || loading}
                                sx={{
                                    alignSelf: 'center',
                                    width: '12rem',
                                    marginTop: '1.5rem',
                                }}
                                type="submit"
                                variant="contained"
                            >
                                Login
                            </LoadingButton>
                            {submitted && countdownTo ? (
                                <Countdown date={countdownTo} renderer={CountdownRenderer} />
                            ) : null}
                        </Stack>
                    </Stack>
                </form>
            </FormProvider>
        </>
    );
}

const GoogleSignInButton = memo(() => {
    const { googleHtmlNonce, onSignInWithGoogle } = useSignInWithGoogle();

    useEffect(() => {
        // NOTE: This is a workaround for the Google Sign-In button - https://stackoverflow.com/a/71421555
        window.handleCredentialResponse = onSignInWithGoogle;
    }, [onSignInWithGoogle]);

    return googleHtmlNonce ? (
        <>
            <div
                key={`g_id_onload_${googleHtmlNonce}`}
                id="g_id_onload"
                data-client_id="974956733042-m04onqjvib6or8b0u30jtil55bt0n7vl.apps.googleusercontent.com"
                data-context="signin"
                data-ux_mode="popup"
                data-callback="handleGoogleCredentialResponse"
                data-nonce={googleHtmlNonce}
                data-auto_prompt="false"
                data-use_fedcm_for_prompt="true"
            ></div>

            <div
                key={`g_id_signin_${googleHtmlNonce}`}
                // class="g_id_signin"
                className="g_id_signin"
                data-type="standard"
                data-shape="rectangular"
                data-theme="outline"
                data-text="signin_with"
                data-size="large"
                data-logo_alignment="left"
                data-width="250"
            ></div>
        </>
    ) : null;
});

const CountdownRenderer: CountdownRendererFn = ({ seconds, completed }) => {
    if (completed) {
        // Render a completed state
        return null;
    } else {
        // Render a countdown
        return (
            <Stack alignItems={'center'} sx={{ paddingY: 2 }}>
                <Alert severity="success">
                    <AlertTitle>Email Sent!</AlertTitle>
                </Alert>
                <Typography variant="body2">{`Check your inbox for the login link`}</Typography>
                <Typography variant="body2">{`OR`}</Typography>
                <Typography variant="body2">{`Send a new email in ${seconds} seconds`}</Typography>
            </Stack>
        );
    }
};
